import {readonly, ref} from 'vue';
import {FetchError} from 'ofetch';
import {useLogger} from '#imports';

type LoadableOptions = {
    alertAboutErrors: boolean;
    fetchErrorHandler: ((e: FetchError) => void) | undefined;
};

/**
 * Generic component to log fetch errors and optionally notify them.
 *
 * Example usage
 * ``` ts
 * import {TJoySnackbarLevels, pushVJoySnackbar} from '@maltjoy/core-vue';
 * import { MyApi } from '@api';
 * import {apiConfiguration} from '~/configuration/apiConfiguration';
 *
 * const myApi = new MyApi(apiConfiguration);
 * const {execute: fetchMyApi, loading } = useLoadable((params)=> myApi.call( params));
 * try {
 *   await fetchMyApi( params);
 * } catch (e) {
 *    // alerting and changing loading state is handled by default
 * }
 *
 * ```
 *  
 * You can customize alerting if you need
 * ``` ts
 * import {TJoySnackbarLevels, pushVJoySnackbar} from '@maltjoy/core-vue';
 * import {useFetchErrorHandler} from '#imports';
 * import { MyApi } from '@api';
 * import {apiConfiguration} from '~/configuration/apiConfiguration';
 *
 * const myApi = new MyApi(apiConfiguration);
 * const {handleFetchError: fetchErrorHandler} = useFetchErrorHandler();
 * const {execute: fetchMyApi, loading } = useLoadable(
 *   (params)=> myApi.call( params),
 *   {
 *     alertAboutErrors: true,
 *     fetchErrorHandler = (error) => fetchErrorHandler(
 *       error,
 *       t('domain.usecase.myapi.call.error'),
 *       (message)=>pushVJoySnackbar({props: {message: errorMessage, level: 'error'}})
 *     );
 *   }
 * );
 * try {
 *   await fetchMyApi( params);
 * } catch (e) {
 *    // changing loading state is handled by default, alerting is handled and customized
 * }
 * ```
 * 
 * You can disable alerting entirely if you need
 * ``` ts
 * import {TJoySnackbarLevels, pushVJoySnackbar} from '@maltjoy/core-vue';
 * import { MyApi } from '@api';
 * import {apiConfiguration} from '~/configuration/apiConfiguration';
 *
 * const myApi = new MyApi(apiConfiguration);
 * const {execute: fetchMyApi, loading } = useLoadable((params)=> myApi.call( params), {alertAboutErrors: false});
 * try {
 *   await fetchMyApi( params);
 * } catch (e) {
 *    // changing loading state is handled by default,
 *    // alerting is disabled so you can recover from the exception silently
 * }
 *
 * ```
 *

 *
 */
export function useLoadable<TData, TArgs extends any[]>(
    handler: (...args: TArgs) => Promise<TData>,
    options: LoadableOptions = {
        alertAboutErrors: true,
        fetchErrorHandler: undefined,
    },
) {
    const logger = useLogger();
    const loading = ref(false);

    function defaultErrorHandler(error: FetchError, customErrorMsg?: string, notifyError?: (message: string) => void) {
        logger.error(error);
        const errorMessage = customErrorMsg || (error.message ?? `${error}`);
        notifyError?.(errorMessage);
    }

    async function execute(...args: TArgs): Promise<TData> {
        try {
            loading.value = true;
            return await handler(...args);
        } catch (e) {
            if (e instanceof Error && 'cause' in e && e.cause instanceof FetchError) {
                if (options.alertAboutErrors) {
                    (options.fetchErrorHandler || defaultErrorHandler)(e.cause);
                } else {
                    logger.info(e);
                }
            }
            // We only handle fetch errors, other errors have to be handled by the caller
            return Promise.reject(e);
        } finally {
            loading.value = false;
        }
    }

    return {
        loading: readonly(loading),
        execute,
    };
}
